<template>
  <div
    ref="searchSelectRootRef"
    v-click-outside="handleClickOutside"
    class="search-select"
    data-qa="search-select-field"
  >
    <div class="search-select__control">
      <TextField
        v-model="model"
        :label="label"
        :errors="textFieldErrors"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        :aria-expanded="showDropdown ? 'true' : 'false'"
        :aria-controls="`${componentId}-listbox`"
        :aria-activedescendant="activeDescendantId"
        v-bind="textFieldAttrs"
        @focus="handleFocus"
        @keydown="handleTextFieldKeyDown"
      >
        <template #prepend>
          <slot name="prepend">
            <SearchIcon class="search-select__search-icon" />
          </slot>
        </template>
        <template #append>
          <slot name="append">
            <KDSSpinner
              v-if="showSpinner"
              class="search-select__spinner"
            />
            <button
              v-else-if="showClearButton"
              type="button"
              class="search-select__clear-button"
              data-qa="search-select-clear-button"
              aria-label="Clear search input"
              @pointerdown.prevent
              @click="handleClearClick"
            >
              <CloseIcon class="search-select__clear-icon" />
            </button>
          </slot>
        </template>
      </TextField>

      <Transition name="search-select-dropdown">
        <div
          v-if="showDropdown"
          class="search-select__dropdown"
          data-qa="search-select-field-dropdown"
        >
          <div
            v-if="hasVisibleOptions"
            class="search-select__list-container"
            @touchmove.passive="handleListTouchMove"
          >
            <div
              v-if="showFeaturedItems && featuredLabel"
              class="search-select__featured-label kds-typography-ui-strong-small"
            >
              {{ featuredLabel }}
            </div>
            <ul
              :id="`${componentId}-listbox`"
              class="search-select__list"
              role="listbox"
            >
              <li
                v-for="(entry, index) in visibleOptions"
                :key="entry.key"
              >
                <button
                  :id="`${componentId}-option-${entry.key}`"
                  type="button"
                  class="search-select__option kds-typography-interactive-medium"
                  :class="{
                    'search-select__option--active': isOptionActive(index),
                  }"
                  role="option"
                  :aria-selected="isOptionActive(index) ? 'true' : 'false'"
                  :data-qa="`search-select-option-${entry.key}`"
                  :data-dd-action-name="optionActionName"
                  @mousedown.prevent
                  @click="selectItem(entry.item)"
                >
                  <slot
                    name="option"
                    :item="entry.item"
                    :segments="entry.segments"
                    :subtitle-segments="entry.subtitleSegments"
                  >
                    <SearchSelectHighlight :segments="entry.segments" />
                  </slot>
                </button>
              </li>
            </ul>
          </div>

          <div
            v-if="slots['dropdown-footer']"
            class="search-select__dropdown-footer"
            :class="{
              'search-select__dropdown-footer--with-border': hasVisibleOptions,
            }"
            data-qa="search-select-dropdown-footer"
          >
            <slot name="dropdown-footer" />
          </div>
        </div>
      </Transition>
    </div>

    <BaseBanner
      v-if="showNoResultsBanner"
      variant="warning"
      data-qa="search-select-no-results"
      :title="noResultsTitle"
      :description="noResultsDescription"
    />

    <BaseModal
      ref="searchErrorModalRef"
      :title="searchErrorTitle"
    >
      <p
        v-if="searchErrorDescription"
        class="kds-typography-paragraph-large"
      >
        {{ searchErrorDescription }}
      </p>

      <template #footer>
        <div class="search-select__modal-actions">
          <BaseButton
            size="small"
            variant="outlined"
            data-qa="search-select-error-cancel"
            @click="handleSearchErrorCancel"
          >
            {{ $t('generic__third_party_url_confirmation__secondary_label') }}
          </BaseButton>
          <BaseButton
            size="small"
            data-qa="search-select-error-retry"
            @click="handleSearchErrorRetry"
          >
            {{ $t('generic__try_again') }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useAttrs,
  useSlots,
  watch,
} from 'vue'
import TextField from '@shared/components/form/TextField.vue'
import KDSSpinner from '@shared/components/KDSSpinner.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseModal from '@shared/components/BaseModal.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import SearchIcon from '@shared/assets/icon/SearchIcon.vue'
import CloseIcon from '@shared/assets/icon/CloseIcon.vue'
import SearchSelectHighlight from '@shared/components/form/SearchSelectHighlight.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  searchFn: {
    type: Function,
    required: true,
  },
  featuredFn: {
    type: Function,
    default: null,
  },
  itemKey: {
    type: Function,
    required: true,
  },
  itemTitle: {
    type: Function,
    required: true,
  },
  itemSubtitle: {
    type: Function,
    default: null,
  },
  noResultsTitle: {
    type: String,
    default: '',
  },
  noResultsDescription: {
    type: String,
    default: '',
  },
  searchErrorTitle: {
    type: String,
    default: '',
  },
  searchErrorDescription: {
    type: String,
    default: '',
  },
  featuredLabel: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  minQueryLength: {
    type: Number,
    default: 2,
  },
  // Datadog names a click from stable attributes, and an option's data-qa holds its key
  optionActionName: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['submit-request'])

const model = defineModel({
  type: String,
  default: '',
})

const selectedItemModel = defineModel('selectedItem', {
  type: Object,
  default: null,
})

const attrs = useAttrs()
const slots = useSlots()
const componentId = `search-select-${getCurrentInstance()?.uid ?? 'unknown'}`
const searchSelectRootRef = ref(null)

const searchErrorModalRef = ref(null)

const featuredItems = ref([])
const searchResults = ref([])

const isFocused = ref(false)
const isLoadingFeaturedItems = ref(false)
const lastCompletedSearchQuery = ref('')
const mountedAtMs = ref(0)

const lastFailedQuery = ref('')

let suppressNextSearch = false
let searchTimer = null
let searchAbortController = null
let requestCounter = 0
const SEARCH_DEBOUNCE_MS = 250
const OUTSIDE_CLICK_GUARD_MS = 250
const SEARCH_STATE = {
  IDLE: 'idle',
  DEBOUNCING: 'debouncing',
  LOADING: 'loading',
}
const searchState = ref(SEARCH_STATE.IDLE)
const activeOptionIndex = ref(-1)

const normalizedQuery = computed(() => model.value?.trim()?.toLowerCase() || '')

const showFeaturedItems = computed(
  () =>
    isFocused.value &&
    normalizedQuery.value.length === 0 &&
    featuredItems.value.length > 0,
)

const showSearchItems = computed(
  () =>
    isFocused.value &&
    normalizedQuery.value.length >= props.minQueryLength &&
    searchResults.value.length > 0,
)

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const buildHighlightedSegments = (title) => {
  const query = normalizedQuery.value

  if (showFeaturedItems.value || query.length < props.minQueryLength) {
    return [{ text: title, isMatch: false }]
  }

  const chunks = [
    ...new Set(
      query
        .split(/\s+/)
        .filter((chunk) => chunk.length > 0)
        .map((chunk) => chunk.toLowerCase()),
    ),
  ]

  if (chunks.length === 0) {
    return [{ text: title, isMatch: false }]
  }

  const pattern = chunks.map(escapeRegExp).join('|')
  const regex = new RegExp(`(${pattern})`, 'gi')

  const segments = []
  let lastIndex = 0

  for (const match of title.matchAll(regex)) {
    if (match.index > lastIndex) {
      segments.push({
        text: title.slice(lastIndex, match.index),
        isMatch: false,
      })
    }
    segments.push({ text: match[0], isMatch: true })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < title.length) {
    segments.push({ text: title.slice(lastIndex), isMatch: false })
  }

  return segments.length > 0 ? segments : [{ text: title, isMatch: false }]
}

const visibleOptions = computed(() => {
  const items = showFeaturedItems.value
    ? featuredItems.value
    : showSearchItems.value
      ? searchResults.value
      : []

  return items.flatMap((item, index) => {
    const key = getItemKey(item, index)
    const title = getItemTitle(item)
    if (key === '' || title === '') {
      return []
    }

    const subtitle = getItemSubtitle(item)

    return [
      {
        item,
        key,
        title,
        segments: buildHighlightedSegments(title),
        subtitleSegments: subtitle ? buildHighlightedSegments(subtitle) : [],
      },
    ]
  })
})
const hasVisibleOptions = computed(() => visibleOptions.value.length > 0)

const hasNoResults = computed(
  () =>
    searchState.value === SEARCH_STATE.IDLE &&
    normalizedQuery.value.length >= props.minQueryLength &&
    lastCompletedSearchQuery.value === normalizedQuery.value &&
    searchResults.value.length === 0,
)

const showDropdown = computed(
  () =>
    hasVisibleOptions.value ||
    (slots['dropdown-footer'] && isFocused.value && hasNoResults.value),
)
const activeOption = computed(() => {
  if (
    activeOptionIndex.value < 0 ||
    activeOptionIndex.value >= visibleOptions.value.length
  ) {
    return null
  }

  return visibleOptions.value[activeOptionIndex.value]
})
const activeDescendantId = computed(() =>
  activeOption.value ? `${componentId}-option-${activeOption.value.key}` : null,
)
const textFieldAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'errors')),
)

const rawErrors = computed(() =>
  Array.isArray(attrs.errors) ? attrs.errors : [],
)
const textFieldErrors = computed(() =>
  showDropdown.value ? [] : rawErrors.value,
)

const showNoResultsBanner = computed(
  () =>
    searchState.value === SEARCH_STATE.IDLE &&
    normalizedQuery.value.length >= props.minQueryLength &&
    lastCompletedSearchQuery.value === normalizedQuery.value &&
    searchResults.value.length === 0 &&
    (props.noResultsTitle || props.noResultsDescription),
)
const showSpinner = computed(
  () =>
    searchState.value !== SEARCH_STATE.IDLE ||
    (isLoadingFeaturedItems.value && isFocused.value),
)
const showClearButton = computed(
  () =>
    model.value.length > 0 &&
    !textFieldAttrs.value.disabled &&
    (!isLoadingFeaturedItems.value || !isFocused.value) &&
    searchState.value === SEARCH_STATE.IDLE,
)

const getItemKey = (item, fallbackIndex = '') => {
  const value = props.itemKey(item)
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : String(fallbackIndex)
}

const getItemTitle = (item) => {
  const value = props.itemTitle(item)
  return typeof value === 'string' ? value : ''
}

const getItemSubtitle = (item) => {
  if (!props.itemSubtitle) return ''
  const value = props.itemSubtitle(item)
  return typeof value === 'string' ? value : ''
}

const stopSearchRequest = () => {
  if (searchAbortController) {
    searchAbortController.abort('A newer search request was triggered')
    searchAbortController = null
  }
}

const clearPendingSearch = () => {
  if (searchTimer) {
    clearTimeout(searchTimer)
    searchTimer = null
  }
  if (searchState.value === SEARCH_STATE.DEBOUNCING) {
    searchState.value = SEARCH_STATE.IDLE
  }
}

const isCanceledRequestError = (error) =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

const executeSearch = async (query) => {
  clearPendingSearch()
  stopSearchRequest()

  const requestId = ++requestCounter
  const requestAbortController = new AbortController()
  searchAbortController = requestAbortController
  searchState.value = SEARCH_STATE.LOADING

  try {
    const items = await props.searchFn(query, requestAbortController.signal)
    if (requestId !== requestCounter) return

    searchResults.value = Array.isArray(items) ? items : []
    lastCompletedSearchQuery.value = query
  } catch (error) {
    if (requestId !== requestCounter) return
    if (
      isCanceledRequestError(error) ||
      requestAbortController.signal.aborted
    ) {
      return
    }

    lastFailedQuery.value = query
    searchErrorModalRef.value?.open()
  } finally {
    if (requestId === requestCounter) {
      searchState.value = SEARCH_STATE.IDLE
      searchAbortController = null
    }
  }
}

const queueSearch = (query) => {
  clearPendingSearch()
  searchState.value = SEARCH_STATE.DEBOUNCING

  searchTimer = setTimeout(() => {
    executeSearch(query)
  }, SEARCH_DEBOUNCE_MS)
}

const runInputFlow = (query) => {
  if (query.length < props.minQueryLength) {
    clearPendingSearch()
    stopSearchRequest()
    searchState.value = SEARCH_STATE.IDLE
    searchResults.value = []
    lastCompletedSearchQuery.value = ''
    return
  }

  queueSearch(query)
}

const selectItem = (item) => {
  const selectedTitle = getItemTitle(item)

  selectedItemModel.value = item
  suppressNextSearch = true
  model.value = selectedTitle

  closeDropdown()
  searchSelectRootRef.value?.querySelector('input')?.blur()
  emit('submit-request')
}

const isOptionActive = (index) => index === activeOptionIndex.value

const getScrollBehavior = (event) => {
  if (event?.repeat === true) return 'auto'

  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return 'auto'
  }

  return 'smooth'
}

const navigateOptions = (delta, event) => {
  if (visibleOptions.value.length === 0) return

  const targetIndex =
    activeOptionIndex.value < 0
      ? delta > 0
        ? 0
        : visibleOptions.value.length - 1
      : activeOptionIndex.value + delta
  const boundedIndex = Math.min(
    Math.max(targetIndex, 0),
    visibleOptions.value.length - 1,
  )
  activeOptionIndex.value = boundedIndex
  const targetKey = visibleOptions.value[boundedIndex]?.key
  if (!targetKey) return

  nextTick(() => {
    const optionId = `${componentId}-option-${targetKey}`
    const escapedOptionId = optionId.replaceAll('"', '\\"')
    const optionElement =
      searchSelectRootRef.value?.querySelector?.(`[id="${escapedOptionId}"]`) ||
      document.getElementById(optionId)

    optionElement?.scrollIntoView({
      behavior: getScrollBehavior(event),
      block: 'nearest',
    })
  })
}

const loadFeaturedItems = async () => {
  if (!props.featuredFn || isLoadingFeaturedItems.value) return

  isLoadingFeaturedItems.value = true

  try {
    const items = await props.featuredFn()
    featuredItems.value = Array.isArray(items) ? items : []
  } catch (error) {
    console.warn('failed loading featured items', error)
  } finally {
    isLoadingFeaturedItems.value = false
  }
}

const shouldAutofocus = () =>
  attrs.autofocus === '' ||
  attrs.autofocus === true ||
  attrs.autofocus === 'true'

const openDropdown = () => {
  if (isFocused.value) return

  isFocused.value = true
}

const handleFocus = () => {
  openDropdown()

  if (
    normalizedQuery.value.length === 0 &&
    props.featuredFn &&
    featuredItems.value.length === 0
  ) {
    void loadFeaturedItems()
  }
}

const handleListTouchMove = () => {
  const activeElement = document.activeElement
  const inputElement = searchSelectRootRef.value?.querySelector?.('input')

  if (activeElement && activeElement === inputElement) {
    inputElement.blur()
  }
}

const handleTextFieldKeyDown = (event) => {
  if (!event) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      navigateOptions(1, event)
      return
    case 'ArrowUp':
      event.preventDefault()
      navigateOptions(-1, event)
      return
    case 'Enter':
      event.preventDefault()
      event.stopPropagation()
      if (activeOption.value) selectItem(activeOption.value.item)
      return
    default:
      return
  }
}

const handleSearchErrorCancel = () => {
  searchErrorModalRef.value?.close()
}

const handleSearchErrorRetry = async () => {
  searchErrorModalRef.value?.close()

  if (!lastFailedQuery.value) return

  await executeSearch(lastFailedQuery.value)
}

const clearInput = async () => {
  selectedItemModel.value = null
  model.value = ''
  activeOptionIndex.value = -1

  if (props.featuredFn && featuredItems.value.length === 0) {
    void loadFeaturedItems()
  }

  await nextTick()

  const inputElement = searchSelectRootRef.value?.querySelector?.('input')
  inputElement?.focus()
  openDropdown()
}

const handleClearClick = async () => {
  await clearInput()
}

const closeDropdown = () => {
  if (!isFocused.value) return

  isFocused.value = false
  activeOptionIndex.value = -1
}

const handleClickOutside = (event) => {
  if (Date.now() - mountedAtMs.value < OUTSIDE_CLICK_GUARD_MS) return
  // Clearing the input removes the clear button before the document click
  // reaches the outside-click directive, so that stale target must not close
  // the reopened dropdown.
  if (event?.target instanceof Node && !event.target.isConnected) return
  closeDropdown()
}

watch(
  () => model.value,
  (value) => {
    const query = value?.trim()?.toLowerCase() || ''

    if (suppressNextSearch) {
      suppressNextSearch = false
      return
    }

    const selectedTitle =
      selectedItemModel.value == null
        ? ''
        : getItemTitle(selectedItemModel.value)

    if (
      selectedItemModel.value != null &&
      query !== selectedTitle.toLowerCase()
    ) {
      selectedItemModel.value = null
    }

    runInputFlow(query)
  },
)

watch(visibleOptions, (options) => {
  if (options.length === 0) {
    activeOptionIndex.value = -1
    return
  }

  if (activeOptionIndex.value >= options.length) {
    activeOptionIndex.value = -1
  }
})

onMounted(async () => {
  mountedAtMs.value = Date.now()
  await loadFeaturedItems()

  if (shouldAutofocus() && normalizedQuery.value.length === 0) {
    openDropdown()
  }
})

onUnmounted(() => {
  clearPendingSearch()
  stopSearchRequest()
})
</script>

<style lang="scss" scoped>
.search-select {
  @include flex-container(column, flex-start, sm);
  width: 100%;

  &__control {
    position: relative;
    width: 100%;
  }

  &__spinner {
    width: $kds-space-3x;
    height: $kds-space-3x;
  }

  &__search-icon {
    display: block;
  }

  &__clear-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: $kds-color-text-primary;
  }

  &__clear-icon {
    display: block;
    width: $kds-space-2x;
    height: $kds-space-2x;
  }

  &__dropdown {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: -4px;
    border-radius: 0 0 $kds-space-1x $kds-space-1x;
    border: 2px solid $kds-color-line-on-interactive;
    border-top-width: 4px;
    background-color: $kds-color-background-subtle;
    box-sizing: border-box;
    max-height: min(40vh, 240px);
    overflow-y: hidden;
    overflow-x: hidden;
  }

  &__featured-label {
    color: $kds-color-text-primary;
    text-transform: uppercase;
    padding: $kds-space-1x $kds-space-2x 0;
  }

  &__list-container {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__list {
    margin: 0;
    padding: $kds-space-1x 0;
    list-style: none;
  }

  &__option {
    width: 100%;
    min-height: 48px;
    text-align: left;
    background: transparent;
    border: none;
    padding: $kds-space-1_5x $kds-space-2x;
    color: $kds-color-text-primary;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      outline: none;
      background-color: $kds-color-background-interactive-subtle-hover;
    }

    &--active {
      outline: none;
      background-color: $kds-color-background-interactive-subtle-hover;
    }
  }

  &__dropdown-footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    margin-top: auto;
    background-color: $kds-color-background-subtle;

    &--with-border {
      border-top: 2px solid $kds-color-line-on-interactive;
    }
  }

  &__modal-actions {
    @include flex-container(row, flex-end, sm);
  }

  :deep(.wrapper__container) {
    position: relative;
    z-index: 3;
  }

  :deep(.wrapper__container--append) {
    display: flex;
    align-items: center;
  }
}

.search-select-dropdown-enter-active,
.search-select-dropdown-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}

.search-select-dropdown-enter-from,
.search-select-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.search-select :deep(.vertical-show-hide-enter-active),
.search-select :deep(.vertical-show-hide-leave-active) {
  transition: none;
}
</style>
